import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import MiniLoading from '../../Components/MiniLoading'
import Cotizacion from '../../Components/Cotizacion'
import Tareas from '../../Components/Tareas'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown } from '@fortawesome/free-solid-svg-icons'
import Navigation from '../../Components/Navigation'
import Pagination from '../../Components/Pagination'
import './misTareas.css'
import Col from 'react-bootstrap/Col'
import Row from 'react-bootstrap/Row'
import {
  fetchSummaryTask,
  getTaskTypesAction,
} from 'redux/actions/tasksActions'
import SalesOrderApprove from 'Components/SalesOrderApprove/SalesOrderApprove'
import useValidatePermission from 'Hooks/useValidatePermission'
import { VAPermissions } from 'Utils/constants'

function MisTareas() {
  const dispatch = useDispatch()

  // CONSTANTS
  const [tasks, setTasks] = useState([])
  const [, setCotizacionUpdated] = useState(false)
  const [search, setSearch] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [, setShowModal] = useState(false)
  const [tasksTypes, setTasksTypes] = useState([])

  // DOCUMENT TYPE FILTER
  const [typeValue, setTypeValue] = useState(null)
  const [typeName, setTypeName] = useState(null)

  // STATUS FILTER
  const [statusList, setStatusList] = useState([])
  const [status, setStatus] = useState(null)
  const [errorMessage, setErrorMessage] = useState(null)

  // PAGINATION LOGIC
  const [currentPage, setCurrentPage] = useState(1)
  const [registers, setRegisters] = useState(null)
  const [firstIndex, setFirstIndex] = useState(0)
  const [lastIndex, setLastIndex] = useState(3)

  const pageSize = 16
  const pageNums = []
  const totalPages = Math.ceil((registers || 0) / pageSize)

  // REDUX STATE
  const salesAgentId = useSelector(
    state => state.login?.user.CustomerInfo?.CustomerId
  )

  for (let i = 1; i <= totalPages; i++) {
    pageNums.push(i)
  }

  const generals = {
    flex: '45%',
  }

  const canChangePriceODV = useValidatePermission(
    VAPermissions.priceOverrideAuthODV
  )

  const fetchTaskTypes = async () => {
    const response = await dispatch(getTaskTypesAction())
    if (response?.data?.Data) {
      setTasksTypes(response.data.Data)
      setStatusList(response.data.Data[0].Status)
    } else {
      console.error('Error:', response.toString())
      throw new Error(response.toString())
    }
  }

  useEffect(() => {
    fetchTaskTypes()
  }, [])

  const fetchTasks = async (
    typeVal = typeValue,
    statusId = status?.StatusId,
    searchText = search,
    pageNum = currentPage
  ) => {
    setTasks([])
    setErrorMessage(null)
    setIsLoading(true)
    setShowModal(false)

    const payload = {
      Type: typeVal,
      StatusId: statusId || '',
      SalesAgentId: salesAgentId,
      WarehouseId: null,
      SearchText: searchText,
      AppType: null,
      PageSize: pageSize,
      PageNumber: pageNum,
    }

    const response = await dispatch(fetchSummaryTask(payload))
    if (response && response.data?.Data.length > 0) {
      const tasksData = response.data.Data
      const filteredTasks = canChangePriceODV
        ? tasksData
        : tasksData.filter(task => {
            // Excluir solo si es SALE_ORDER y está Pendiente
            return !(
              task.Type === 'SALE_ORDER' &&
              task.StatusDescription === 'Pendiente'
            )
          })
      setTasks(filteredTasks)
      setRegisters(tasksData[0]?.TotalRegisters || 0)
    } else {
      setTasks([])
      setRegisters(0)
      setErrorMessage('Sin Resultados.')
      console.error('Error:', response?.toString() || 'Respuesta vacía')
    }
    setIsLoading(false)
  }

  // Efecto para ejecutar búsqueda cuando cambia la página
  useEffect(() => {
    if (typeValue && status) {
      fetchTasks(typeValue, status?.StatusId, search, currentPage)
    }
  }, [currentPage])

  const handleType = type => {
    setTypeName(type.Name)
    setTypeValue(type.Code)
    setStatusList(type.Status)
    setStatus(null)
    // Resetear paginación pero mantener la búsqueda activa
    setCurrentPage(1)
    setLastIndex(3)
    setFirstIndex(0)
  }

  const handleSelectedStatus = status => {
    setStatus(status)
    // Resetear paginación pero mantener la búsqueda activa
    setCurrentPage(1)
    setLastIndex(3)
    setFirstIndex(0)
  }

  const taskTypesMemo = useMemo(() => {
    if (!canChangePriceODV) {
      return tasksTypes.filter(type => type.Code !== 'SALE_ORDER')
    }
    return tasksTypes
  }, [canChangePriceODV, tasksTypes])

  // Función auxiliar para ejecutar búsqueda
  const performSearch = () => {
    setCurrentPage(1)
    setLastIndex(3)
    setFirstIndex(0)

    // Ejecutar búsqueda DIRECTAMENTE
    fetchTasks(typeValue, status?.StatusId, search, 1)
  }

  const handleSearchInput = e => {
    const value = e.target.value || ''
    setSearch(value.trim())
  }

  return (
    <>
      <Navigation />
      <section className="tareas-main" data-testid="container-my-tasks">
        <div className="tareas-header" data-testid="container-my-tasks-header">
          <h2>Mis Tareas</h2>
        </div>
        <div
          className="tareas-content"
          data-testid="container-my-tasks-content">
          <Row className="tareas-search ">
            <Col md={6} lg={5} className="tareas-search-bar">
              <div className="position-relative">
                <input
                  data-testid="input-search-my-tasks"
                  value={search}
                  disabled={isLoading}
                  type="text"
                  placeholder="Buscar"
                  onChange={handleSearchInput}
                />
              </div>
            </Col>
            <Col md={6} lg={3}>
              <FilterSelector
                label={typeName}
                options={taskTypesMemo}
                getOptionValue={item => item.Code}
                getOptionLabel={item => item.Name}
                value={typeValue}
                onChange={handleType}
                disabled={isLoading}
                placeholder="Selecciona un tipo de documento"
                testId="document-type-my-tasks"
              />
            </Col>
            <Col md={6} lg={2}>
              <FilterSelector
                label={status?.Name || 'Estado'}
                options={statusList}
                getOptionValue={item => item.StatusId}
                getOptionLabel={item => item.Name}
                value={status?.StatusId}
                onChange={handleSelectedStatus}
                disabled={isLoading}
                placeholder="Selecciona el estado"
                testId="status-my-tasks"
              />
            </Col>
            <Col md={6} lg={1}>
              <button
                data-testid="btn-search-my-tasks"
                className="button-save"
                disabled={!(typeValue !== null && status !== null) || isLoading}
                onClick={() => {
                  performSearch()
                }}>
                {isLoading ? 'Buscando...' : 'Buscar'}
              </button>
            </Col>
          </Row>
        </div>
        {isLoading ? (
          <MiniLoading />
        ) : (
          <>
            <div className="tareas-listado" data-testid="container-task-list">
              <div
                className="tareas-listado-left"
                data-testid="container-task-list-left">
                {tasks &&
                  tasks.slice(0, 8).map(task => {
                    if (task.Type === 'DOC_QUOTE') {
                      return (
                        <Cotizacion
                          key={task.DocumentNumber}
                          cotizacion={task}
                          generals={generals}
                          setCotizacionUpdated={setCotizacionUpdated}
                        />
                      )
                    } else if (task.Type === 'SALE_ORDER') {
                      return (
                        <SalesOrderApprove
                          key={task.DocumentNumber}
                          saleOrder={task}
                          generals={generals}
                          canApprove={canChangePriceODV}
                        />
                      )
                    } else {
                      return (
                        <Tareas
                          key={task.Key ? task.Key : task.Date}
                          keyProp={task.Key ? task.Key : task.Date}
                          tarea={task}
                          generals={generals}
                        />
                      )
                    }
                  })}
              </div>
              <div
                className="tareas-listado-right"
                data-testid="container-task-list-right">
                {tasks &&
                  tasks.slice(8, 16).map(task => {
                    if (task.Type === 'DOC_QUOTE') {
                      return (
                        <Cotizacion
                          key={task.DocumentNumber}
                          cotizacion={task}
                          generals={generals}
                          setCotizacionUpdated={setCotizacionUpdated}
                        />
                      )
                    } else if (task.Type === 'SALE_ORDER') {
                      return (
                        <SalesOrderApprove
                          key={task.DocumentNumber}
                          saleOrder={task}
                          generals={generals}
                          canApprove={canChangePriceODV}
                        />
                      )
                    } else {
                      return (
                        <Tareas
                          key={task.Key ? task.Key : task.Date}
                          keyProp={task.Key ? task.Key : task.Date}
                          tarea={task}
                          generals={generals}
                        />
                      )
                    }
                  })}
              </div>
            </div>
            {errorMessage ? (
              <div
                className="error-message-mis-tareas"
                data-testid="container-error-message-my-tasks">
                <p>{errorMessage}</p>
              </div>
            ) : (
              <div
                className="mis-tareas-pagination"
                data-testid="container-pagination-my-tasks">
                <Pagination
                  type={'paginationTarea'}
                  firstIndex={firstIndex}
                  lastIndex={lastIndex}
                  currentPage={currentPage}
                  pageNums={pageNums}
                  setCurrentPage={setCurrentPage}
                  setFirstIndex={setFirstIndex}
                  setLastIndex={setLastIndex}
                />
              </div>
            )}
          </>
        )}
      </section>
    </>
  )
}

export default MisTareas

/**
 * FilterSelector - Componente reutilizable de selector
 *
 * @param {string} label - Texto a mostrar en el header
 * @param {array} options - Array de opciones a mostrar
 * @param {function} getOptionValue - Función que retorna el valor de la opción (ej: item => item.Code)
 * @param {function} getOptionLabel - Función que retorna la etiqueta de la opción (ej: item => item.Name)
 * @param {any} value - Valor seleccionado
 * @param {function} onChange - Callback cuando se selecciona una opción. Recibe el objeto completo
 * @param {boolean} disabled - Desabilitar el selector
 * @param {string} placeholder - Texto cuando no hay selección
 */
function FilterSelector({
  label,
  options = [],
  getOptionValue,
  getOptionLabel,
  value,
  onChange,
  disabled = false,
  placeholder = 'Seleccionar',
  testId = '',
}) {
  const [isOpen, setIsOpen] = useState(false)
  const containerRef = useRef(null)

  // Validar que las funciones existan
  if (!getOptionValue || !getOptionLabel) {
    console.error(
      'FilterSelector: getOptionValue y getOptionLabel son requeridos'
    )
    return null
  }

  // Cerrar selector al hacer clic fuera
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false)
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const handleSelect = option => {
    onChange(option)
    setIsOpen(false)
  }

  const isOptionSelected = option => {
    return getOptionValue(option) === value
  }

  const hasMatchingOption = options.some(
    option => getOptionValue(option) === value
  )
  const isEmpty =
    (value === null || value === undefined || value === '') &&
    !hasMatchingOption

  return (
    <div
      className="filter-selector"
      ref={containerRef}
      data-testid={testId ? `select-${testId}` : undefined}
      onClick={() => !disabled && setIsOpen(!isOpen)}>
      <div className={`filter-selector-header ${disabled ? 'disabled' : ''}`}>
        <span
          className={`filter-label ${
            isEmpty ? 'filter-label-placeholder' : ''
          }`}>
          {isEmpty ? placeholder : label}
        </span>
        <FontAwesomeIcon
          className={`chevron ${isOpen ? 'open' : ''}`}
          icon={faChevronDown}
        />
      </div>

      {isOpen && (
        <ul
          className="filter-selector-list"
          data-testid={testId ? `select-${testId}-options` : undefined}>
          {options && options.length > 0 ? (
            options.map((option, index) => {
              const optionValue = getOptionValue(option)
              const optionLabel = getOptionLabel(option)
              const isSelected = isOptionSelected(option)

              return (
                <li
                  key={optionValue || index}
                  className={`filter-selector-item ${
                    isSelected ? 'selected' : ''
                  }`}
                  data-testid={
                    testId
                      ? `select-${testId}-option-${optionValue}`
                      : undefined
                  }
                  onClick={e => {
                    e.stopPropagation()
                    handleSelect(option)
                  }}>
                  <label htmlFor={`filter-${optionValue}`}>{optionLabel}</label>
                </li>
              )
            })
          ) : (
            <li className="filter-selector-item disabled">
              <span>No hay opciones disponibles</span>
            </li>
          )}
        </ul>
      )}
    </div>
  )
}
